import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';

import { api } from '../api/cliente';
import type { Archivo } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import { Aviso, Campo, Cargando, Encabezado, Seleccion, Tarjeta, Vacio } from '../ui/piezas';
import Panorama, { type PuntoEsferico } from '../ui/Panorama';
import EditorPlanos, { type Plano } from '../ui/EditorPlanos';

/**
 * El constructor del recorrido 360° de un inmueble.
 *
 * <p>La API tenía todo esto desde hace tiempo —escenas, puntos de paso, planos—
 * y no había forma de usarlo: sin pantalla, un recorrido solo se podía armar a
 * golpe de peticiones a mano. La ficha del portal ya calculaba si un inmueble
 * tenía recorrido, y no podía tenerlo nunca.
 *
 * <p>Va en su propia ruta y no dentro del formulario del inmueble, que ya es
 * largo: armar un recorrido es una sesión de trabajo aparte, con la panorámica
 * ocupando media pantalla.
 *
 * <p>Los puntos de paso se colocan **pulsando sobre la panorámica**. Escribir
 * un yaw y un pitch a mano es pedirle a alguien que adivine dos ángulos; el
 * visor devuelve los de donde se pulsó.
 */

interface Hotspot {
  id: string;
  tipo: 'NAVEGACION' | 'INFO' | 'ENLACE';
  yaw: number;
  pitch: number;
  escenaDestinoId: string | null;
  titulo: string | null;
  contenido: string | null;
  url: string | null;
  icono: string | null;
}

interface Escena {
  id: string;
  nombre: string;
  orden: number;
  esInicial: boolean;
  imagen: string | null;
  yawInicial: number | null;
  pitchInicial: number | null;
  fovInicial: number | null;
  norteOffset: number | null;
  /** Donde queda sobre el plano, en porcentaje. Nulos si aun no se ubico. */
  planoId: string | null;
  planoX: number | null;
  planoY: number | null;
  hotspots: Hotspot[];
}

interface Tour {
  escenaInicial: string | null;
  escenas: Escena[];
  planos: Plano[];
}

export default function Recorrido() {
  const { id = '' } = useParams();
  const cache = useQueryClient();
  const { puede } = useSesion();
  const entrada = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [activa, setActiva] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState('');
  /** El nombre de la escena activa, editable. */
  const [nombreEscena, setNombreEscena] = useState('');
  /** Hacia donde mira el visor ahora. Lo informa el propio visor al soltar. */
  const [mirando, setMirando] = useState<PuntoEsferico | null>(null);

  /** El punto de paso a medio colocar: primero se pulsa, después se dice a dónde va. */
  const [colocando, setColocando] = useState<PuntoEsferico | null>(null);
  const [destino, setDestino] = useState('');

  const editable = puede('inmueble:editar');

  const tour = useQuery({
    queryKey: ['recorrido', id],
    queryFn: () => api<Tour>(`/admin/inmuebles/${id}/recorrido`),
    /*
     * Mientras haya una escena sin imagen se vuelve a preguntar: la panorámica
     * se procesa en segundo plano y hasta que no termina no hay nada que ver.
     */
    refetchInterval: (consulta) => {
      const datos = consulta.state.data;
      const faltaAlguna =
        (datos?.escenas ?? []).some((e) => !e.imagen) ||
        (datos?.planos ?? []).some((p) => !p.imagen);
      return faltaAlguna ? 4000 : false;
    },
  });

  const escenas = tour.data?.escenas ?? [];
  const escena = useMemo(
    () => escenas.find((e) => e.id === activa) ?? escenas[0],
    [escenas, activa],
  );

  const refrescar = (mensaje: string) => {
    setError(null);
    setExito(mensaje);
    void cache.invalidateQueries({ queryKey: ['recorrido', id] });
  };

  /**
   * Sube la panorámica y crea su escena.
   *
   * <p>Son dos pasos y no uno porque el archivo y la escena son cosas
   * distintas: el mismo archivo podría servir a otra entidad, y la escena lleva
   * el nombre, el orden y sus puntos de paso.
   */
  const subir = async (archivos: FileList | null) => {
    const archivo = archivos?.[0];
    if (!archivo) return;
    setSubiendo(true);
    setError(null);
    try {
      const formulario = new FormData();
      formulario.append('archivo', archivo);
      const subido = await api<Archivo>('/admin/archivos?tipo=PANORAMA_360', {
        metodo: 'POST',
        formulario,
      });
      await api<Tour>(`/admin/inmuebles/${id}/recorrido/escenas`, {
        metodo: 'POST',
        cuerpo: {
          archivoId: subido.id,
          nombre: nombreNuevo.trim() || archivo.name.replace(/\.[^.]+$/, ''),
          esInicial: escenas.length === 0,
        },
      });
      setNombreNuevo('');
      refrescar('Escena añadida. La panorámica se está procesando.');
    } catch (e) {
      setError(e);
      setExito(null);
    } finally {
      setSubiendo(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  const renombrar = useMutation({
    mutationFn: (datos: { escenaId: string; nombre: string }) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/escenas/${datos.escenaId}`, {
        metodo: 'PUT',
        cuerpo: { nombre: datos.nombre },
      }),
    onSuccess: () => refrescar('Nombre guardado.'),
    onError: (e) => setError(e),
  });

  const marcarInicial = useMutation({
    mutationFn: (escenaId: string) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/escenas/${escenaId}`, {
        metodo: 'PUT',
        cuerpo: { nombre: escenas.find((e) => e.id === escenaId)?.nombre, esInicial: true },
      }),
    onSuccess: () => refrescar('Es la escena de entrada.'),
    onError: (e) => setError(e),
  });

  /** Guarda la vista actual como la que se ve al entrar en la escena. */
  const guardarVista = useMutation({
    mutationFn: (datos: { escenaId: string; yaw: number; pitch: number }) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/escenas/${datos.escenaId}`, {
        metodo: 'PUT',
        cuerpo: {
          nombre: escenas.find((e) => e.id === datos.escenaId)?.nombre,
          yawInicial: datos.yaw,
          pitchInicial: datos.pitch,
        },
      }),
    onSuccess: () => refrescar('Vista de entrada guardada.'),
    onError: (e) => setError(e),
  });

  const borrarEscena = useMutation({
    mutationFn: (escenaId: string) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/escenas/${escenaId}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      setActiva(null);
      refrescar('Escena eliminada.');
    },
    onError: (e) => setError(e),
  });

  const crearPaso = useMutation({
    mutationFn: (datos: { punto: PuntoEsferico; destinoId: string }) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/hotspots`, {
        metodo: 'POST',
        cuerpo: {
          escenaOrigenId: escena?.id,
          tipo: 'NAVEGACION',
          yaw: Math.round(datos.punto.yaw * 100) / 100,
          pitch: Math.round(datos.punto.pitch * 100) / 100,
          escenaDestinoId: datos.destinoId,
          titulo: escenas.find((e) => e.id === datos.destinoId)?.nombre ?? 'Ir',
        },
      }),
    onSuccess: () => {
      setColocando(null);
      setDestino('');
      refrescar('Punto de paso creado.');
    },
    onError: (e) => setError(e),
  });

  const borrarPaso = useMutation({
    mutationFn: (hotspotId: string) =>
      api<Tour>(`/admin/inmuebles/${id}/recorrido/hotspots/${hotspotId}`, { metodo: 'DELETE' }),
    onSuccess: () => refrescar('Punto de paso eliminado.'),
    onError: (e) => setError(e),
  });

  if (tour.isPending) return <Cargando />;

  const otras = escenas.filter((e) => e.id !== escena?.id);

  return (
    <>
      <Encabezado
        titulo="Recorrido 360°"
        nota="Una escena por espacio, y los puntos de paso entre ellas."
      />
      <p style={{ marginTop: -6 }}>
        <Link to={`/inmuebles/${id}`}>← Volver al inmueble</Link>
      </p>

      <Aviso error={error} exito={exito} />

      {editable && (
        <Tarjeta titulo="Añadir una escena">
          <div className="rejilla">
            <Campo
              etiqueta="Nombre de la escena"
              valor={nombreNuevo}
              alCambiar={setNombreNuevo}
              ayuda="Sala, cocina, alcoba principal… Si se deja vacío se usa el nombre del archivo."
            />
            <div>
              <label>Panorámica equirectangular</label>
              <input
                ref={entrada}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={subiendo}
                onChange={(e) => void subir(e.target.files)}
              />
              <p className="campo__ayuda">
                {subiendo
                  ? 'Subiendo…'
                  : 'Una foto 360° de las que saca una cámara panorámica, en proporción 2:1.'}
              </p>
            </div>
          </div>
        </Tarjeta>
      )}

      {escenas.length === 0 ? (
        <Vacio>
          Este inmueble no tiene recorrido. Sube la primera panorámica y ve añadiendo escenas: los
          puntos de paso entre ellas se colocan pulsando sobre la imagen.
        </Vacio>
      ) : (
        <>
          <Tarjeta titulo="Escenas">
            <div className="fila" style={{ flexWrap: 'wrap', gap: 8 }}>
              {escenas.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className={e.id === escena?.id ? 'secundario' : 'plano'}
                  onClick={() => {
                    setActiva(e.id);
                    setColocando(null);
                    setNombreEscena(e.nombre);
                    setMirando(null);
                  }}
                >
                  {e.nombre}
                  {e.esInicial && ' · entrada'}
                  {!e.imagen && ' · procesando'}
                </button>
              ))}
            </div>
          </Tarjeta>

          {escena && (
            <Tarjeta titulo={escena.nombre}>
              {!escena.imagen ? (
                <Vacio>
                  La panorámica se está procesando. Esta pantalla se actualiza sola en cuanto esté
                  lista.
                </Vacio>
              ) : (
                <>
                  <Panorama
                    src={escena.imagen}
                    yawInicial={escena.yawInicial ?? 0}
                    pitchInicial={escena.pitchInicial ?? 0}
                    alto={440}
                    alPulsar={editable ? (p) => setColocando(p) : undefined}
                    alMirar={setMirando}
                    hotspots={escena.hotspots.map((h) => ({
                      id: h.id,
                      yaw: h.yaw,
                      pitch: h.pitch,
                      titulo: h.titulo ?? 'Punto de paso',
                      etiqueta: h.titulo ?? '→',
                    }))}
                    alPulsarHotspot={(hotspotId) => {
                      const h = escena.hotspots.find((x) => x.id === hotspotId);
                      if (h?.escenaDestinoId) setActiva(h.escenaDestinoId);
                    }}
                  />

                  {colocando && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        border: '1px solid var(--linea)',
                        borderRadius: 8,
                        background: 'var(--fondo)',
                      }}
                    >
                      <p className="campo__ayuda" style={{ marginTop: 0 }}>
                        Punto marcado en {colocando.yaw.toFixed(1)}° / {colocando.pitch.toFixed(1)}°.
                        ¿A qué escena lleva?
                      </p>
                      {otras.length === 0 ? (
                        <p className="campo__ayuda">
                          Hace falta otra escena para poder pasar a alguna parte.
                        </p>
                      ) : (
                        <>
                          <Seleccion
                            etiqueta="Lleva a"
                            valor={destino}
                            alCambiar={setDestino}
                            vacio="Elige la escena"
                            opciones={otras.map((e) => ({ valor: e.id, texto: e.nombre }))}
                          />
                          <div className="fila" style={{ marginTop: 10 }}>
                            <button
                              type="button"
                              className="secundario"
                              disabled={destino === '' || crearPaso.isPending}
                              onClick={() =>
                                crearPaso.mutate({ punto: colocando, destinoId: destino })
                              }
                            >
                              Crear el punto de paso
                            </button>
                            <button
                              type="button"
                              className="plano"
                              onClick={() => setColocando(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {editable && (
                    <div style={{ marginTop: 14, maxWidth: 380 }}>
                      <Campo
                        etiqueta="Nombre de la escena"
                        valor={nombreEscena || escena.nombre}
                        alCambiar={setNombreEscena}
                      />
                      <button
                        type="button"
                        className="plano"
                        style={{ marginTop: 6 }}
                        disabled={
                          nombreEscena.trim() === '' ||
                          nombreEscena.trim() === escena.nombre ||
                          renombrar.isPending
                        }
                        onClick={() =>
                          renombrar.mutate({
                            escenaId: escena.id,
                            nombre: nombreEscena.trim(),
                          })
                        }
                      >
                        Guardar el nombre
                      </button>
                    </div>
                  )}

                  {editable && (
                    <div className="fila" style={{ marginTop: 14, flexWrap: 'wrap', gap: 8 }}>
                      {!escena.esInicial && (
                        <button
                          type="button"
                          className="plano"
                          onClick={() => marcarInicial.mutate(escena.id)}
                        >
                          Usar como escena de entrada
                        </button>
                      )}
                      <button
                        type="button"
                        className="plano"
                        title="Guarda hacia dónde mira el visitante al entrar en esta escena"
                        onClick={() =>
                          guardarVista.mutate({
                            escenaId: escena.id,
                            // La vista actual del visor: se lee de donde quedó
                            // tras arrastrar, que es lo que se está viendo.
                            yaw: mirando?.yaw ?? escena.yawInicial ?? 0,
                            pitch: mirando?.pitch ?? escena.pitchInicial ?? 0,
                          })
                        }
                      >
                        Guardar esta vista como la de entrada
                      </button>
                      <button
                        type="button"
                        className="plano"
                        onClick={() => borrarEscena.mutate(escena.id)}
                      >
                        Eliminar la escena
                      </button>
                    </div>
                  )}

                  {escena.hotspots.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <h3 style={{ fontSize: '0.78rem', textTransform: 'uppercase' }}>
                        Puntos de paso de esta escena
                      </h3>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.88rem' }}>
                        {escena.hotspots.map((h) => (
                          <li key={h.id} style={{ marginBottom: 4 }}>
                            {h.titulo ?? 'Sin título'} · {h.yaw.toFixed(0)}° /{' '}
                            {h.pitch.toFixed(0)}°
                            {editable && (
                              <button
                                type="button"
                                className="plano"
                                style={{ marginLeft: 8 }}
                                onClick={() => borrarPaso.mutate(h.id)}
                              >
                                Quitar
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </Tarjeta>
          )}
        </>
      )}

      {/* El plano va despues de las escenas porque sus zonas se enlazan a
          ellas: sin escenas, una zona solo puede marcar. */}
      <EditorPlanos
        inmuebleId={id}
        planos={tour.data?.planos ?? []}
        escenas={escenas.map((e) => ({
          id: e.id,
          nombre: e.nombre,
          planoId: e.planoId,
          planoX: e.planoX,
          planoY: e.planoY,
        }))}
        editable={editable}
      />
    </>
  );
}
